import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Bell,
  AlertTriangle,
  Info,
  CheckCircle,
  AlertCircle,
  X,
  Trash2,
  Eye,
  History,
  Check,
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { toast } from 'sonner';

export default function Notifications() {
  const {
    notifications,
    loading,
    backendConnected,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    deleteNotification,
  } = useNotifications();

  const [showHistory, setShowHistory] = useState(false);

  const getIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'alert':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const getBadgeColor = (type: string) => {
    switch (type) {
      case 'warning':
        return 'bg-orange-100 text-orange-800';
      case 'alert':
        return 'bg-red-100 text-red-800';
      case 'success':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id);
    toast.success('Notification marked as read');
  };

  const handleDismiss = async (id: string) => {
    await dismissNotification(id);
    toast.success('Notification dismissed');
  };

  const handleDelete = async (id: string) => {
    await deleteNotification(id);
    toast.success('Notification deleted');
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    toast.success('All notifications marked as read');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-3"></div>
          <p className="text-gray-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  // Filter notifications based on view mode
  const displayedNotifications = showHistory
    ? notifications // Show all notifications
    : notifications.filter(n => !n.dismissed); // Show only active notifications

  const unreadNotifications = displayedNotifications.filter(n => !n.read);
  const readNotifications = displayedNotifications.filter(n => n.read);

  return (
    <div className="space-y-4">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h3 className="font-semibold text-gray-900">
            {showHistory ? 'Notification History' : 'Notifications'}
          </h3>
          {!backendConnected && (
            <Badge className="bg-orange-100 text-orange-800 text-xs">
              Offline
            </Badge>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => setShowHistory(!showHistory)}
            variant="outline"
            size="sm"
            className="h-8"
          >
            {showHistory ? (
              <>
                <Bell className="h-4 w-4 mr-1" />
                Active
              </>
            ) : (
              <>
                <History className="h-4 w-4 mr-1" />
                History
              </>
            )}
          </Button>
          {unreadNotifications.length > 0 && (
            <Button
              onClick={handleMarkAllAsRead}
              variant="outline"
              size="sm"
              className="h-8"
            >
              <Check className="h-4 w-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Connection Status */}
      <div className="flex items-center space-x-2 text-sm">
        <div className={`w-2 h-2 rounded-full ${backendConnected ? 'bg-green-500' : 'bg-gray-400'}`}></div>
        <span className="text-gray-600">
          {backendConnected ? 'Synced with database' : 'Using local storage'}
        </span>
      </div>

      {displayedNotifications.length === 0 ? (
        <Card className="p-12 text-center">
          <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium mb-1">
            {showHistory ? 'No notification history' : 'All caught up!'}
          </p>
          <p className="text-sm text-gray-500">
            {showHistory
              ? 'Notifications you receive will appear here'
              : 'You have no new notifications'}
          </p>
        </Card>
      ) : (
        <>
          {/* Unread Notifications */}
          {unreadNotifications.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 px-2">
                Unread ({unreadNotifications.length})
              </h4>
              {unreadNotifications.map((notification) => (
                <Card
                  key={notification.id}
                  className="p-4 bg-blue-50 border-l-4 border-l-blue-500 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold text-gray-900">{notification.title}</h4>
                            <Badge className={getBadgeColor(notification.type)}>
                              {notification.type}
                            </Badge>
                          </div>
                          <p className="text-gray-700 text-sm mb-2">{notification.message}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>{formatTimeAgo(notification.createdAt)}</span>
                            {notification.wellId && (
                              <span className="flex items-center space-x-1">
                                <span>•</span>
                                <span>Well {notification.wellId}</span>
                              </span>
                            )}
                            {(notification as any).forAdmin && (
                              <Badge className="bg-purple-100 text-purple-800 text-xs">
                                Admin
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <Button
                        onClick={() => handleMarkAsRead(notification.id)}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Mark as read"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDismiss(notification.id)}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
                        title="Dismiss"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      {showHistory && (
                        <Button
                          onClick={() => handleDelete(notification.id)}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          title="Delete permanently"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Read Notifications */}
          {readNotifications.length > 0 && (
            <div className="space-y-2">
              {unreadNotifications.length > 0 && (
                <h4 className="text-sm font-medium text-gray-700 px-2 mt-6">
                  Read ({readNotifications.length})
                </h4>
              )}
              {readNotifications.map((notification) => (
                <Card
                  key={notification.id}
                  className="p-4 bg-gray-50 border-gray-200 opacity-75 hover:opacity-100 transition-opacity"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5 opacity-50">
                          {getIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-medium text-gray-700">{notification.title}</h4>
                            <Badge className="bg-gray-200 text-gray-600">
                              {notification.type}
                            </Badge>
                          </div>
                          <p className="text-gray-600 text-sm mb-2">{notification.message}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>{formatTimeAgo(notification.createdAt)}</span>
                            {notification.wellId && (
                              <span className="flex items-center space-x-1">
                                <span>•</span>
                                <span>Well {notification.wellId}</span>
                              </span>
                            )}
                            {(notification as any).forAdmin && (
                              <Badge className="bg-purple-100 text-purple-800 text-xs">
                                Admin
                              </Badge>
                            )}
                            <span className="flex items-center space-x-1">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              <span>Read</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <Button
                        onClick={() => handleDismiss(notification.id)}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
                        title="Dismiss"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      {showHistory && (
                        <Button
                          onClick={() => handleDelete(notification.id)}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          title="Delete permanently"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Stats Footer */}
      {displayedNotifications.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 pt-4 border-t">
          <span>
            {unreadNotifications.length} unread, {readNotifications.length} read
          </span>
          {showHistory && (
            <span className="text-xs text-gray-500">
              {notifications.filter(n => n.dismissed).length} dismissed
            </span>
          )}
        </div>
      )}
    </div>
  );
}
